import { useCallback, useContext, useMemo, useState } from 'react';
import NewExpressionBuilder from '@components/filters/NewExpressionBuilder';
import {
  AvCheckbox,
  AvConfirmDialog,
  AvTooltip,
  DescriptorType,
  FeatureFlags,
  getEmptyFilterExpression,
  getUniqueFieldNames,
  HistoricalFilterModeWarning,
  removeMetricsFromFilter,
  Select,
  SwitchToggle,
  TextInput,
} from '@fabric-ui/components';
import { TimeBucketName } from '@fabric-ui/components/Table';
import { QuestionFull } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Divider, InputLabel, SvgIcon, Typography } from '@mui/material';
import { isMetric, isNullOrUndefined } from '@utils/utils';
import { getFieldTitleByExpression } from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { useAvContext } from '../../../../../context/AvContextProvider';
import { PendoFeature } from '../../../../../utils/pendo/PendoFeature';
import { useCalculatedDimensionsList, useMeasurementsList } from '../../../../Reports/hooks';
import CollapsedSection from '../../../components/CollapsedSection';
import SelectSortingField, { SORTING_KEYS } from '../../../components/SelectSortingField';
import { getKeyByValue } from '../../../components/SelectWidgetType/SelectWidgetType';
import TimeFilter from '../../../components/TimeFilter/TimeFilter';
import { barChartMaxSize, DimensionTypes } from '../../../constants';
import { CustomDashboardContext } from '../../../CustomDashboardContext';
import { getGroupOthersDisabledReason, isHistoricalQuery } from '../../../CustomDashboardsUtils/utils';
import {
  AllTypeWidgets,
  HistoricalFilterMode,
  PivotTableTypeWidget,
  SortDir,
  TableSubType,
  TileSubType,
  TopResults,
  WidgetCategory,
} from '../../../types';
import { FieldType } from '../../../types/types';
import { getWidgetTypeConfig } from '../../../widgetTypeConfig';
import TimeBucketBreakdown from '../../TimeBucketBreakdown/TimeBucketBreakdown';
import { hasTimeBucket } from '../../TimeBucketBreakdown/utils';
import { getSortingPath } from '../utils';
import { validateTopSize } from './utils';

type KeyValueMap<T> = {
  [K in keyof T]?: string;
};

export const HistoricalFilterModeName = {
  [HistoricalFilterMode.CURRENT]: 'Current State',
  [HistoricalFilterMode.HISTORICAL]: 'Historical State',
} as const satisfies Record<HistoricalFilterMode, string>;

const DEFAULT_TOP_SIZE = 10;

const WidgetFilters = () => {
  const {
    featureFlags,
    accountEntities: { aggProjs },
    measurements,
  } = useAvContext();
  const { newWidget, setNewWidget, getDisplayName } = useContext(CustomDashboardContext);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const {
    filter,
    projectionId: { name },
    select: { dims, metrics },
    metricsFilterThreshold,
  } = newWidget.requests[0];

  const top = newWidget.requests[0].top || ({ size: undefined, groupOthers: false } as TopResults);

  const isHistoricalWidget = isHistoricalQuery(newWidget.requests[0]);
  const sortAndTopConfig = getWidgetTypeConfig(newWidget.category, newWidget.type).filters?.sortAndTop;

  const [errors, setErrors] = useState({} as KeyValueMap<TopResults>);

  const topSizeWidgetValue = newWidget.type === TableSubType.Table ? newWidget.definition.custom.topN : top.size;
  const toTopSizeString = (v: number | null | undefined) => (v !== null && v !== undefined ? String(v) : String(DEFAULT_TOP_SIZE));

  const [topSize, setTopSize] = useState<string>(() => toTopSizeString(topSizeWidgetValue));
  const [groupOthersInput, setGroupOthersInput] = useState<boolean>(top.groupOthers);

  const groupOthersDisabledReason = getGroupOthersDisabledReason(newWidget);

  const activeEntityId = aggProjs[name].entityTypeId.name;

  const metricsFilterFunc = useCallback(v => v.context.includes(activeEntityId), [activeEntityId]);

  const customMeasurements = useMeasurementsList({
    entityName: activeEntityId,
    filterFunc: metricsFilterFunc,
    onSuccess: d =>
      d.data.map(({ systemName, displayName, type, description }) => ({
        value: systemName,
        pathAlias: systemName,
        title: displayName,
        group: getKeyByValue(FieldType, FieldType.Measurements),
        entityTypeId: { builtin: true },
        type,
        description,
      })),
  });

  const customCalculatedDimensions = useCalculatedDimensionsList({
    projectionName: name,
    filterFunc: v => v.context?.some(ctx => ctx.name === name),
    onSuccess: d =>
      d.data.map(({ systemName, displayName, type }) => ({
        value: systemName,
        pathAlias: systemName,
        title: displayName,
        group: 'Calculated Dimensions',
        entityTypeId: { builtin: true },
        type: type?.kindWrapper?.kind ? DescriptorType[type.kindWrapper.kind] : type,
      })),
  });

  const isHistoricalEvaluationMode = isHistoricalWidget && newWidget.historicalFilterMode === HistoricalFilterMode.HISTORICAL;

  const fieldsList = useMemo(
    () =>
      aggProjs[name].fieldList.INTERACTIVE.filter(f => !f.visibilityConfig.config.hidden).concat(
        isHistoricalEvaluationMode ? [] : customMeasurements,
        customCalculatedDimensions
      ),
    [name, customMeasurements, customCalculatedDimensions, isHistoricalEvaluationMode]
  );

  const sizeLabel = newWidget.type === TableSubType.PivotTable ? 'Page Size' : 'Show Top';
  const topSizeMax = newWidget.category === WidgetCategory.Bar ? barChartMaxSize : undefined;

  const onTopSizeChange = (value: number | null) => {
    const raw = value === null ? '' : String(value);
    setTopSize(raw);
    if (raw === '') {
      setErrors({ size: validateTopSize(null, groupOthersInput, sizeLabel, topSizeMax)! });
    } else {
      updateTopSizeGroupOthers('size')(value);
    }
  };

  const onTopSizeBlur = () => {
    if (topSize === '') {
      setTopSize(toTopSizeString(topSizeWidgetValue));
    }
  };

  const setFilters = value => {
    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          filter: value,
        },
      ],
    } as AllTypeWidgets);
  };

  const updateTopSizeGroupOthers = (key: Exclude<keyof TopResults, 'offset'>) => value => {
    if (key === 'size') {
      const error = validateTopSize(value, groupOthersInput, sizeLabel, topSizeMax);
      if (error) {
        setErrors({ size: error });
        return;
      }
    }
    if (key === 'groupOthers') {
      setGroupOthersInput(value);
      if (value) {
        const currentSize = topSize === '' ? null : Number(topSize);
        const error = validateTopSize(currentSize, true, sizeLabel, topSizeMax);
        if (error) {
          setErrors({ size: error });
          return;
        }
      }
      setErrors({});
    } else {
      setErrors({});
    }

    if (newWidget.type === TableSubType.Table && key === 'size') {
      setNewWidget({
        ...newWidget,
        definition: {
          ...newWidget.definition,
          custom: {
            ...newWidget.definition.custom,
            topN: value,
          },
        },
        ...((value === 0 || value === null) && {
          requests: [
            {
              ...newWidget.requests[0],
              top: { ...newWidget.requests[0].top, groupOthers: false },
            },
          ],
        }),
      } as AllTypeWidgets);
      return;
    }

    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          top: {
            ...top,
            [key]: value,
            ...(key === 'groupOthers' && value ? { size: top.size || DEFAULT_TOP_SIZE } : {}),
          },
        },
      ],
    } as AllTypeWidgets);
  };

  const onGroupOthersChange = updateTopSizeGroupOthers('groupOthers');

  const setMetricsFilterThreshold = (value?: number) =>
    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          metricsFilterThreshold: value,
        },
      ],
    } as AllTypeWidgets);

  const maxColumnsOptions = [{ value: 10, title: 10 }];

  const sortingOptions = useMemo(
    () =>
      [...metrics, ...dims].map(v => {
        const commonProps = v.type ? { type: v.type } : {};

        if (hasTimeBucket(v.name)) {
          return {
            title: TimeBucketName[v.name],
            value: v.name,
            ...commonProps,
          };
        }

        if (v.expression) {
          const displayName = getDisplayName(v.name);
          return { title: getFieldTitleByExpression({ expression: v.expression, displayName }), value: v.alias!, ...commonProps };
        }

        return {
          title: getDisplayName(v?.alias || v?.name),
          value: v?.alias || v?.name,
          ...commonProps,
        };
      }),
    [metrics, dims]
  );

  const rowSortingOptions = useMemo(
    () =>
      sortingOptions.filter(({ type }) =>
        (newWidget as PivotTableTypeWidget).definition.custom.showTotals ? type !== DimensionTypes.Col : type === DimensionTypes.Row
      ),
    [sortingOptions]
  );
  const columnSortingOptions = useMemo(() => sortingOptions.filter(({ type }) => type === DimensionTypes.Col), [sortingOptions]);

  const getSortingValue = (key, value, currentSorting) => {
    if (key === SORTING_KEYS.name && !value) {
      return [];
    }

    const { path: prevPath, ...rest } = currentSorting || {};

    if (key === SORTING_KEYS.name && !isMetric(value, measurements.visible)) {
      return [{ ...rest, [key]: value }];
    }

    const path =
      key === SORTING_KEYS.name && isMetric(value, measurements.visible)
        ? { path: getSortingPath(['pivot', 'rowTotals', value]) }
        : prevPath;

    return [{ ...rest, ...(newWidget.type === TableSubType.PivotTable ? { path } : {}), [key]: value }];
  };

  const setSorting = (key: keyof typeof SORTING_KEYS, value: SortDir | string, sortingKey: 'sorting' | 'sortingCol') => {
    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          [sortingKey]: getSortingValue(key, value, newWidget.requests[0][sortingKey][0]),
        },
      ],
    } as PivotTableTypeWidget);
  };

  const onChangeFilterMode = v => {
    const currentFilter = newWidget.requests[0].filter;
    if (currentFilter && v === HistoricalFilterMode.HISTORICAL) {
      const fieldNames = Array.from(getUniqueFieldNames(currentFilter)) as string[];
      const hasMetricFilter = fieldNames.some(fieldName => measurements.asObject[fieldName]);

      if (hasMetricFilter) {
        setIsPopupOpen(true);
        return;
      }
    }
    setNewWidget({ ...newWidget, historicalFilterMode: v });
  };

  const onSwitchToHistoricalFilter = () => {
    const currentFilter = newWidget.requests[0].filter;
    const newFilter = currentFilter ? removeMetricsFromFilter(currentFilter, measurements.asObject) : currentFilter;

    setNewWidget({
      ...newWidget,
      historicalFilterMode: HistoricalFilterMode.HISTORICAL,
      requests: [
        {
          ...newWidget.requests[0],
          filter: newFilter,
        },
      ],
    } as AllTypeWidgets);
    setIsPopupOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, ...flex.col, pt: 2, gap: 2, overflow: 'auto' }}>
      {featureFlags[FeatureFlags.HistoricalAnalytics] && newWidget.type !== TileSubType.Trend && (
        <CollapsedSection title="Date" isFocus={false}>
          <Box sx={{ ...flex.col, gap: 2 }}>
            <TimeFilter isMultiBreakdown />
            {newWidget.requests[0].timeRange && <TimeBucketBreakdown isMultiBreakdown />}
          </Box>
        </CollapsedSection>
      )}

      <CollapsedSection title="Sort & Top" isFocus={false}>
        <Box sx={{ ...flex.col, gap: 2 }}>
          <Box sx={{ ...flex.col, gap: 1 }}>
            <SelectSortingField
              label={newWidget.type === TableSubType.PivotTable ? 'Sort Row by' : 'Sort by'}
              selectedOption={newWidget.requests[0].sorting[0]}
              options={newWidget.type === TableSubType.PivotTable ? rowSortingOptions : sortingOptions}
              onChange={(key, value) => setSorting(key, value, 'sorting')}
            />

            <Box sx={{ ...flex.justifyBetweenEnd }}>
              {!sortAndTopConfig?.showTop?.hidden && (
                <TextInput
                  sx={{ maxWidth: 130 }}
                  label={sizeLabel}
                  size="xSmall"
                  type="number"
                  value={topSize}
                  onChange={onTopSizeChange}
                  onBlur={onTopSizeBlur}
                  error={errors.size}
                  disableClearButton
                  data-pendo-id={newWidget.type === TableSubType.Table ? PendoFeature.TableWidgetShowTop : undefined}
                />
              )}
              {!sortAndTopConfig?.groupOthers?.hidden && (
                <AvTooltip title={groupOthersDisabledReason ?? ''}>
                  <span>
                    <AvCheckbox
                      size="small"
                      indeterminate={false}
                      value={groupOthersInput}
                      onChange={onGroupOthersChange}
                      label="Group Others"
                      disabled={!!groupOthersDisabledReason}
                    />
                  </span>
                </AvTooltip>
              )}
            </Box>

            {newWidget.type === TableSubType.PivotTable && (
              <>
                <SelectSortingField
                  label="Sort Column by"
                  selectedOption={newWidget.requests[0].sortingCol?.[0]}
                  options={columnSortingOptions}
                  onChange={(...args) => setSorting(...args, 'sortingCol')}
                />

                <Select
                  size="xSmall"
                  label={
                    <Box sx={{ ...flex.row, gap: '6px' }}>
                      Max Columns
                      <AvTooltip
                        title={
                          'Specify the maximum number of unique column dimension values. ' +
                          'Each value will then produce columns for each corresponding measure.'
                        }
                        popperProps={{ sx: { '.MuiTooltip-tooltip': { width: '420px', maxWidth: 'none' } } }}>
                        <div>
                          <QuestionFull />
                        </div>
                      </AvTooltip>
                    </Box>
                  }
                  value={maxColumnsOptions[0].value}
                  options={maxColumnsOptions}
                  disabled
                />
              </>
            )}

            <Box sx={{ ...flex.justifyBetweenCenter, pt: 1 }}>
              <InputLabel sx={{ fontSize: '12px', mb: 0 }}>Exclude zero values</InputLabel>
              <SwitchToggle
                value={!isNullOrUndefined(metricsFilterThreshold)}
                onChange={v => setMetricsFilterThreshold(v ? 0 : undefined)}
              />
            </Box>
          </Box>
          <Divider />
        </Box>
      </CollapsedSection>

      <CollapsedSection title="Conditions Filters" isFocus={false}>
        {isHistoricalWidget && featureFlags[FeatureFlags.WidgetHistoricalFilterMode] ? (
          <>
            <Select
              showClearSelection={false}
              label={
                <Typography variant="caption" display="flex" alignItems="center" gap={0.25}>
                  Filter Evaluation Time
                  <AvTooltip
                    muiProps={{ placement: 'top' }}
                    title="Apply filters either to the current state of the data or to its historical state at the time of the query.">
                    <SvgIcon component="span" sx={{ width: 16, height: 16, ...flex.center }}>
                      <QuestionFull style={{ width: 16, height: 16 }} viewBox="0 0 20 20" />
                    </SvgIcon>
                  </AvTooltip>
                </Typography>
              }
              size="xSmall"
              options={Object.values(HistoricalFilterMode).map(v => ({ title: HistoricalFilterModeName[v], value: v }))}
              value={newWidget.historicalFilterMode || HistoricalFilterMode.CURRENT}
              onChange={onChangeFilterMode}
              muiProps={{
                disableClearable: true,
              }}
            />

            <Divider sx={{ my: 2 }} />
          </>
        ) : null}
        <NewExpressionBuilder
          hasListTab={featureFlags[FeatureFlags.CustomDashboardsFilterListTab]}
          fields={fieldsList}
          filter={filter || getEmptyFilterExpression()}
          setFilter={setFilters}
          isVertical
          size="xSmall"
          defaultField={`${aggProjs[name].pathAlias}._key`}
          fieldsListAdornment={isHistoricalEvaluationMode && <HistoricalFilterModeWarning />}
          isHistorical={!!newWidget.requests[0].timeRange}
          activeProjName={newWidget.requests[0].projectionId.name}
          showAdvanced={!newWidget.requests[0].timeRange}
        />
      </CollapsedSection>

      <AvConfirmDialog
        isOpen={isPopupOpen}
        title="Measurement Filter Will Be Removed"
        body={
          <span>
            Measurement filtering is only available in the current state.
            <br />
            Switching to historical mode will remove your applied filters,
            <br />
            would you like to proceed?
          </span>
        }
        confirmButton={{
          text: 'Yes',
          onClick: onSwitchToHistoricalFilter,
        }}
        onClose={() => setIsPopupOpen(false)}
        closeButton={{ onClick: () => setIsPopupOpen(false) }}
      />
    </Box>
  );
};

export default WidgetFilters;
